import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://bufkinlaw.com";
const OG_IMAGE =
  "https://customer-assets.emergentagent.com/job_legal-finance-3/artifacts/wh5zrplf_No-Background-4.png";

// Search Console verification tokens — replace with real values after first
// verification visit (or wire to environment variables).
const GOOGLE_SITE_VERIFICATION = "TQzKDckEJiTFQe9WRH1OmXMptcaYHgzrWdaJPb0J6TI";
const BING_SITE_VERIFICATION = "F2C2C38CDD01B1EE30459FCE9F5699E4";

// Google Business Profile (used in sameAs / external knowledge graph linking)
const GOOGLE_BUSINESS_PROFILE_URL = "https://share.google/BGUYnb28M1Yh89lcL";

/**
 * SEO + AEO component for per-page meta, Open Graph, Twitter Card,
 * canonical URLs, and JSON-LD structured data.
 *
 * Props:
 *  title         - <title> + og:title + twitter:title
 *  description   - meta description, og:description, twitter:description
 *  path          - e.g. "/practice-areas/litigation" (used for canonical + og:url)
 *  image         - absolute OG image URL (optional, defaults to portrait)
 *  type          - og:type (default 'website')
 *  noindex       - if true, sets robots noindex,nofollow
 *  schemas       - array of schema.org JSON-LD objects
 *  keywords      - optional comma-separated keywords string
 */
export const Seo = ({
  title,
  description,
  path = "/",
  image = OG_IMAGE,
  type = "website",
  noindex = false,
  schemas = [],
  keywords,
}) => {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title.includes("Bufkin")
    ? title
    : `${title} | The Bufkin Law Firm`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content="Richard L. Bufkin — Attorney, CPA, and AICPA-certified Forensic Accountant" />
      <meta property="og:site_name" content="The Bufkin Law Firm" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content="Richard L. Bufkin — Attorney, CPA, and AICPA-certified Forensic Accountant" />

      {/* Search Console verification — render only when real tokens are configured */}
      {GOOGLE_SITE_VERIFICATION && !GOOGLE_SITE_VERIFICATION.startsWith("REPLACE_") && (
        <meta name="google-site-verification" content={GOOGLE_SITE_VERIFICATION} />
      )}
      {BING_SITE_VERIFICATION && !BING_SITE_VERIFICATION.startsWith("REPLACE_") && (
        <meta name="msvalidate.01" content={BING_SITE_VERIFICATION} />
      )}

      {/* JSON-LD schemas */}
      {schemas.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

// ---------------- Schema builders ----------------

const ORG_BASE = {
  "@type": ["Attorney", "LegalService", "LocalBusiness"],
  "@id": `${SITE_URL}/#organization`,
  name: "The Bufkin Law Firm",
  alternateName: "Bufkin Law",
  url: SITE_URL,
  logo: "https://customer-assets.emergentagent.com/job_legal-finance-3/artifacts/jf349uv5_logo-1.png",
  image: OG_IMAGE,
  telephone: "+1-214-415-1759",
  email: "dick@bufkinlaw.com",
  priceRange: "$$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "126 W. Second Street",
    addressLocality: "Mt. Pleasant",
    addressRegion: "TX",
    postalCode: "75455",
    addressCountry: "US",
  },
  areaServed: [
    { "@type": "State", name: "Texas" },
    { "@type": "City", name: "Dallas" },
    { "@type": "City", name: "Fort Worth" },
    { "@type": "City", name: "Mt. Pleasant" },
  ],
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "17:00",
  },
  sameAs: [GOOGLE_BUSINESS_PROFILE_URL],
};

export const PERSON_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${SITE_URL}/richard-bufkin#person`,
  name: "Richard L. Bufkin",
  alternateName: "Dick Bufkin",
  givenName: "Richard",
  familyName: "Bufkin",
  jobTitle: "Attorney & Forensic Accountant",
  description:
    "Texas attorney, Certified Public Accountant, and AICPA-certified forensic accountant with over 50 years of experience in business, family-law, probate, and bankruptcy litigation. One of very few attorneys nationwide holding the AICPA Certified in Financial Forensics (CFF) credential.",
  image: OG_IMAGE,
  url: `${SITE_URL}/richard-bufkin`,
  telephone: "+1-214-415-1759",
  email: "dick@bufkinlaw.com",
  worksFor: { "@id": `${SITE_URL}/#organization` },
  workLocation: {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      streetAddress: "126 W. Second Street",
      addressLocality: "Mt. Pleasant",
      addressRegion: "TX",
      postalCode: "75455",
      addressCountry: "US",
    },
  },
  hasCredential: [
    { "@type": "EducationalOccupationalCredential", credentialCategory: "license", name: "Attorney at Law (Texas)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "Certified Public Accountant (CPA)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "AICPA Certified in Financial Forensics (CFF)", dateCreated: "2008-05" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "degree", name: "Master of Laws in Taxation (LL.M.)", educationalLevel: "Master" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "degree", name: "Master of Business Administration (MBA)", educationalLevel: "Master" },
  ],
  alumniOf: [
    { "@type": "EducationalOrganization", name: "Southern Methodist University, Dedman School of Law" },
  ],
  knowsAbout: [
    "Forensic Accounting",
    "Bankruptcy & Insolvency Litigation",
    "Fraud Detection",
    "Economic Damages",
    "Business Litigation",
    "Family Law Litigation",
    "Expert Witness Testimony",
    "Financial Statement Misrepresentation",
    "Fraudulent Transfers",
    "Breach of Fiduciary Duty",
    "Estate Planning & Probate",
  ],
  memberOf: [
    { "@type": "Organization", name: "American Institute of Certified Public Accountants (AICPA)" },
    { "@type": "Organization", name: "State Bar of Texas" },
    { "@type": "Organization", name: "AICPA Forensic and Valuation Services Section" },
  ],
};

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  ...ORG_BASE,
  description:
    "Forensic accounting and financially oriented legal matters. Business, family-law, probate and bankruptcy litigation led by Richard L. Bufkin — attorney, CPA, and AICPA-certified forensic accountant.",
  founder: { "@id": `${SITE_URL}/richard-bufkin#person` },
  employee: { "@id": `${SITE_URL}/richard-bufkin#person` },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Legal & Forensic Accounting Services",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Litigation — Business, Family-Law, Probate & Creditor" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Financial Consulting & Testifying Expert Services" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Non-Litigation Legal Services (Transactional, Estate, Probate)" } },
    ],
  },
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "The Bufkin Law Firm",
  description: "Forensic accounting and financially oriented legal matters — Texas.",
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en-US",
};

// FAQ schema builder
export const buildFaqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a,
    },
  })),
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: ["[data-speakable='true']"],
  },
});

// Breadcrumb schema builder
export const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

// Service schema (per practice area)
export const buildServiceSchema = ({ name, description, slug, areaServed = "Texas" }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE_URL}/practice-areas/${slug}#service`,
  serviceType: name,
  name,
  description,
  provider: { "@id": `${SITE_URL}/#organization` },
  areaServed: { "@type": "State", name: areaServed },
  url: `${SITE_URL}/practice-areas/${slug}`,
});

// WebPage schema
export const buildWebPageSchema = ({
  title,
  description,
  path,
  datePublished = "2026-01-01",
  dateModified = new Date().toISOString().slice(0, 10),
}) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${SITE_URL}${path}#webpage`,
  url: `${SITE_URL}${path}`,
  name: title,
  description,
  inLanguage: "en-US",
  isPartOf: { "@id": `${SITE_URL}/#website` },
  about: { "@id": `${SITE_URL}/#organization` },
  datePublished,
  dateModified,
  primaryImageOfPage: OG_IMAGE,
});

// Article schema (for blog posts) — citable by Google AI Overviews & Perplexity
export const buildArticleSchema = ({
  title,
  description,
  slug,
  datePublished,
  dateModified,
  keywords,
  bodyText,
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "@id": `${SITE_URL}/insights/${slug}#article`,
  headline: title,
  description,
  url: `${SITE_URL}/insights/${slug}`,
  mainEntityOfPage: `${SITE_URL}/insights/${slug}`,
  image: OG_IMAGE,
  datePublished,
  dateModified: dateModified || datePublished,
  inLanguage: "en-US",
  keywords,
  articleBody: bodyText,
  author: { "@id": `${SITE_URL}/richard-bufkin#person` },
  publisher: { "@id": `${SITE_URL}/#organization` },
  isPartOf: { "@id": `${SITE_URL}/#website` },
});

// Blog listing schema
export const buildBlogSchema = (insights) => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  "@id": `${SITE_URL}/insights#blog`,
  name: "Insights from The Bufkin Law Firm",
  description:
    "Long-form analysis on forensic accounting, business litigation, and financial expert witness work by Richard L. Bufkin — attorney, CPA, and AICPA CFF.",
  url: `${SITE_URL}/insights`,
  publisher: { "@id": `${SITE_URL}/#organization` },
  blogPost: insights.map((i) => ({
    "@type": "BlogPosting",
    headline: i.title,
    description: i.excerpt,
    url: `${SITE_URL}/insights/${i.slug}`,
    datePublished: i.datePublished,
    author: { "@id": `${SITE_URL}/richard-bufkin#person` },
  })),
});
